import { UserMeVM } from '@app/models/user-me.model';
import { Component, OnInit, Input } from '@angular/core';
import {
  FieldLogReqs,
  JobFieldLogVM,
  JobDateVM,
  EquipmentMachineVM,
  EquipmentUntrackedClassVM,
  JobNoBoxReason,
  JobDateEquipTimecard,
} from '@app/models';
import { ModalController } from '@ionic/angular';
import {
  ClockOutRequest,
  ClockOutPayload,
} from '@services/api/job-date.service';
import { differenceInMinutes, parseISO, min, addDays, format } from 'date-fns';
import { Point } from '@shared/utils/distanceBetweenPoints';
import { take } from 'rxjs/operators';
import { JobNoBoxReasonFacade } from '@app/facades/job-no-box-reason.facade';
import { JobDateEquipTimecardFacade } from '@app/facades/job-date-equip-timecard.facade';
import { EquipmentClassFacade } from '@app/facades/equipment-class.facade';

export interface FieldLogTotalTimePayload {
  hours: number;
  minutes: number;
}

export interface FieldLogTimecardPayload {
  id: string;
  user: string;
  clockIn: string;
  clockOut: string;
  breakMinutes: number;
  skipMorningBreak: boolean;
  skipAfternoonBreak: boolean;
}

export interface FieldLogEquipmentPayload {
  id?: string;
  equip: number;
  hours: number;
  minutes: number;
  options: FieldLogEquipOptPayload[];
}

export interface FieldLogEquipOptPayload {
  id?: string;
  equipOption: number;
  hours: number;
  minutes: number;
}

export interface FieldLogUntrackedEquipPayload {
  id?: string;
  equip: number;
  hours: number;
  minutes: number;
}

export interface FieldLogFormResult {
  payload: ClockOutPayload;
  forwardCosting: boolean;
}

export interface FieldLogPayload {
  fieldLogId: string;
  nightPay?: boolean;
  unscheduled?: boolean;
  timecards?: FieldLogTimecardPayload[];
  timecard?: FieldLogTimecardPayload;
  totalTime?: FieldLogTotalTimePayload;
  equipment?: FieldLogEquipmentPayload[];
  untracked?: FieldLogUntrackedEquipPayload[];
}

@Component({
  selector: 'app-clock-out-modal',
  templateUrl: './clock-out-modal.component.html',
  styleUrls: ['./clock-out-modal.component.scss'],
})
export class ClockOutModalComponent implements OnInit {
  @Input() fieldLogReqs: FieldLogReqs;
  @Input() fieldLog: JobFieldLogVM;
  @Input() jobDate: JobDateVM;
  @Input() currentLocation: Point | null = null;
  @Input() user: UserMeVM;
  @Input() equipment: { [key: number]: EquipmentMachineVM };
  @Input() equipUntracked: EquipmentUntrackedClassVM[];

  activeTimecardId = -1;
  activeTimecard: FieldLogTimecardPayload;

  maxDate: Date;

  steps: string[] = [];
  step = 0;

  forwardCosting = false;

  noBoxReason = '';

  payload: FieldLogPayload = {
    fieldLogId: '',
  };

  private equipClassNames: { [id: number]: string } = {};
  private savedEquipTimecards: JobDateEquipTimecard[] = [];

  constructor(
    private modalCtrl: ModalController,
    private noBoxReasonFacade: JobNoBoxReasonFacade,
    private equipTimecardFacade: JobDateEquipTimecardFacade,
    private equipClassFacade: EquipmentClassFacade,
  ) {}

  ngOnInit() {
    this.activeTimecardId = this.fieldLog.timecards.findIndex(
      tc => tc.clockOut === null
    );
    this.payload.fieldLogId = this.fieldLog.id;
    if (this.fieldLogReqs.timecards) {
      this.setupTimecards();
    }
    if (this.fieldLogReqs.totalTime) {
      this.setupTotalTime();
    }
    if (this.fieldLogReqs.equipment) {
      this.setupEquip();
      this.setupUntrackedEquip();
    }
    if (this.fieldLogReqs.costing) {
      this.setupCosting();
    }
    if (this.fieldLogReqs.extras) {
      this.setupExtras();
    }

    this.steps.push('summary');

    if (this.fieldLogReqs.equipment && this.qualifiesForBoxCheck()) {
      this.equipClassFacade.all$.pipe(take(1)).subscribe(classes => {
        classes.forEach(c => { this.equipClassNames[c.id] = c.name; });
      });
      this.equipTimecardFacade.all$.pipe(take(1)).subscribe(timecards => {
        this.savedEquipTimecards = timecards;
        this.updateNoBoxStep();
      });
    }
  }

  setupTimecards() {
    this.steps.push('timecards');
    this.payload.timecards = this.fieldLog.timecards.map(tc => {
      return {
        id: tc.id,
        user: tc.user.id,
        clockIn: tc.clockIn.toISOString(),
        clockOut: tc.clockOut ? tc.clockOut.toISOString() : null,
        breakMinutes: tc.breakMinutes,
        skipMorningBreak: tc.skipMorningBreak ? tc.skipMorningBreak : false,
        skipAfternoonBreak: tc.skipAfternoonBreak
          ? tc.skipAfternoonBreak
          : false,
      };
    });
    this.activeTimecard = this.payload.timecards.find(
      tc => tc.clockOut === null
    );
    const dates = this.user.invalidRanges
      .filter(ir => ir.start >= parseISO(this.activeTimecard.clockIn))
      .map(ir => ir.start);

    this.maxDate =
      dates.length > 0
        ? min(dates)
        : addDays(parseISO(this.activeTimecard.clockIn), 3);

    this.activeTimecard.breakMinutes = 30;
  }

  setupCosting() {
    this.steps.push('costing');
  }

  setupExtras() {
    this.steps.push('extras');
  }

  setupTotalTime() {
    this.steps.push('totaltime');
    this.payload.totalTime = {
      hours: 0,
      minutes: 0,
    };
    this.payload.nightPay = false;
    this.payload.unscheduled = false;
  }

  setupEquip() {
    this.steps.push('equipment');
    this.payload.equipment = this.jobDate.siteEquipment.map(machine => {
      return {
        equip: machine.equip.id,
        hours: 0,
        minutes: 0,
        options: machine.equip.options
          .filter(opt => opt.billable)
          .map(opt => {
            return {
              equipOption: opt.id,
              hours: 0,
              minutes: 0,
            };
          }),
      };
    });
  }

  setupUntrackedEquip() {
    this.steps.push('untracked');
    this.payload.untracked = [];
    this.equipUntracked.forEach(eqCl => {
      eqCl.machines.forEach(mach => {
        this.payload.untracked.push({
          equip: mach.id,
          hours: 0,
          minutes: 0,
        });
      });
    });
    // loop over existing untrackedEquipment in this.fieldLog and fill in values.
  }

  getStep() {
    return this.steps[this.step];
  }

  onClickNext() {
    const currentStep = this.getStep();
    this.step++;
    if (currentStep === 'equipment' || currentStep === 'untracked') {
      this.updateNoBoxStep();
    }
  }

  nextDisabled() {
    if (this.getStep() === 'noBoxReason') {
      return !this.noBoxReason?.trim();
    }
    return (
      !this.activeTimecard.clockOut ||
      parseISO(this.activeTimecard.clockOut) <
        parseISO(this.activeTimecard.clockIn)
    );
  }

  onClickCancel() {
    this.modalCtrl.dismiss();
  }

  onClickSave() {
    this.payload.timecard = this.activeTimecard;
    delete this.payload.timecards;

    if (this.payload.equipment) {
      this.payload.equipment = this.payload.equipment.filter(eq => {
        return eq.minutes > 0 || eq.hours > 0;
      });
    }
    if (this.payload.untracked) {
      this.payload.untracked = this.payload.untracked.filter(eq => {
        return eq.minutes > 0 || eq.hours > 0;
      });
    }
    if (!this.fieldLogReqs.totalTime) {
      delete this.payload.totalTime;
    }

    if (this.steps.includes('noBoxReason') && this.noBoxReason?.trim()) {
      const jobPlanId = this.jobDate?.job?.jobPlanId;
      if (jobPlanId) {
        const reason: JobNoBoxReason = {
          jobPlan: jobPlanId,
          employee: this.user.user.id,
          reason: this.noBoxReason.trim(),
        };
        this.noBoxReasonFacade.create(reason);
      }
    }

    const payload = this.generatePayload(this.payload);
    const result: FieldLogFormResult = {
      payload,
      forwardCosting: this.forwardCosting,
    };
    this.modalCtrl.dismiss(result);
  }

  generatePayload(data: FieldLogPayload): ClockOutPayload {
    const payload: ClockOutRequest = {
      timecard: { ...data.timecard, jobFieldLog: data.fieldLogId },
    };
    if (this.currentLocation) {
      payload.timecard.clockOutLat = this.currentLocation.lat;
      payload.timecard.clockOutLng = this.currentLocation.lng;
    }
    if (data.equipment && data.equipment.length > 0) {
      payload.equipment = data.equipment.map(equip => ({
        equip: equip.equip,
        minutes: equip.hours * 60 + equip.minutes,
        fieldLog: data.fieldLogId,
        extraFieldLog: null,
        options: equip.options
          ? equip.options
              .filter(opt => opt.hours * 60 + opt.minutes > 0)
              .map(opt => ({
                equipOption: opt.equipOption,
                minutes: opt.hours * 60 + opt.minutes,
              }))
          : [],
      }));
    }
    if (data.untracked && data.untracked.length > 0) {
      payload.untracked = data.untracked.map(unt => ({
        fieldLog: data.fieldLogId,
        equip: unt.equip,
        minutes: unt.hours * 60 + unt.minutes,
      }));
    }
    if (data.totalTime) {
      payload.totalTime = {
        jobFieldLog: data.fieldLogId,
        minutes: data.totalTime.hours * 60 + data.totalTime.minutes,
      };
    }
    if (data.nightPay) {
      payload.nightPay = data.nightPay;
    }
    if (data.unscheduled) {
      payload.unscheduled = data.unscheduled;
    }

    return {
      id: data.fieldLogId,
      payload,
    };
  }

  totalMinutes() {
    if (this.payload.totalTime) {
      return this.payload.totalTime.hours * 60 + this.payload.totalTime.minutes;
    } else if (this.activeTimecard) {
      const minutes = differenceInMinutes(
        parseISO(this.activeTimecard.clockOut),
        parseISO(this.activeTimecard.clockIn)
      );
      return minutes - this.activeTimecard.breakMinutes;
    } else {
      return 0;
    }
  }

  private qualifiesForBoxCheck(): boolean {
    const jn = (this.jobDate?.job?.jobNumber ?? '').toUpperCase();
    if (!jn.startsWith('F') && !jn.startsWith('R') && !jn.startsWith('EMG')) {
      return false;
    }
    const firstCrew = this.jobDate?.job?.firstCrewDate;
    if (!firstCrew) { return false; }
    const jobDateStr = format(this.jobDate.date, 'yyyy-MM-dd');
    return jobDateStr === firstCrew;
  }

  private isBoxTrailerMachine(machine: EquipmentMachineVM | undefined): boolean {
    if (!machine) { return false; }
    const typeName = (machine.equipType?.name ?? '').toLowerCase();
    if (typeName === 'box trailer') { return true; }
    const className = (this.equipClassNames[machine.equipType?.equipClass] ?? '').toLowerCase();
    return className === 'trench boxes';
  }

  private isUntrackedTrenchBox(machineId: number): boolean {
    for (const eqCl of (this.equipUntracked ?? [])) {
      const classIsTrenchBox = eqCl.name.toLowerCase().includes('trench box');
      for (const mach of eqCl.machines) {
        if (mach.id === machineId) {
          return classIsTrenchBox || mach.name.toLowerCase().includes('trench box');
        }
      }
    }
    return false;
  }

  private hasBoxTrailerTime(): boolean {
    const sessionHas = (this.payload.equipment ?? []).some(entry => {
      if (entry.hours === 0 && entry.minutes === 0) { return false; }
      return this.isBoxTrailerMachine(this.equipment[entry.equip]);
    });
    if (sessionHas) { return true; }
    const untrackedHas = (this.payload.untracked ?? []).some(entry => {
      if (entry.minutes === 0) { return false; }
      return this.isUntrackedTrenchBox(entry.equip);
    });
    if (untrackedHas) { return true; }
    return this.savedEquipTimecards.some(tc => {
      if (tc.minutes === 0) { return false; }
      return this.isBoxTrailerMachine(this.equipment[tc.equip]);
    });
  }

  private updateNoBoxStep() {
    if (!this.qualifiesForBoxCheck()) { return; }
    const idx = this.steps.indexOf('noBoxReason');
    const untrackedIdx = this.steps.indexOf('untracked');
    const insertIdx = untrackedIdx !== -1 ? untrackedIdx + 1 : this.steps.indexOf('summary');
    if (this.hasBoxTrailerTime()) {
      if (idx !== -1) {
        this.steps.splice(idx, 1);
        if (this.step > idx) { this.step--; }
      }
    } else {
      if (idx === -1) {
        this.steps.splice(insertIdx, 0, 'noBoxReason');
      }
    }
  }
}
